import { useAccountLinks } from '@/app/[lang]/(protected)/account/hooks/use-account-links'
import { useSession } from '@/components/Session/use-session'
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { ACCOUNT_LINKS, PORTFOLIO_LINKS } from '@/constants/links'
import { Link } from '@/i18n/routing'
import { useKYCData } from '@/lib/queries/account'
import { cn } from '@/lib/utils'
import {
  ArrowDownUp,
  BarChart2,
  Check,
  LucideIcon,
  User,
  Wallet,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { useLocaleControl } from './use-locale-control'

type ItemBase = {
  name: string
  isEnabled?: boolean
  Icon?: LucideIcon
  children?: NavItem[]
}

export type NavLinkItem = ItemBase & {
  href: string
}

type ComponentItem = ItemBase & {
  Component: React.ComponentType
}

export type NavItem = NavLinkItem | ComponentItem

export const useNavItems = () => {
  const t = useTranslations()
  const { LanguageIcon, setLocale, currentLocale, availableLocales } =
    useLocaleControl()
  const { isAuthenticated, logout } = useSession()
  const { hasKYC, isPending } = useKYCData()
  const accountLinks = useAccountLinks()

  const componentItems = {
    kyc: {
      name: 'kyc',
      isEnabled: isAuthenticated && !hasKYC && !isPending,
    },
    languageDropdown: {
      name: 'language',
      Component: () => null,
      Icon: LanguageIcon,
      children: availableLocales.map(({ value, label }) => ({
        name: label,
        Component: () => (
          <DropdownMenuItem
            key={value}
            textValue={value}
            onSelect={() => setLocale(value)}
            className={value === currentLocale ? 'text-primary-alt' : ''}
          >
            {label}
          </DropdownMenuItem>
        ),
      })),
    },
    accountDropdown: {
      name: 'account',
      Component: () => null,
      Icon: User,
      children: [
        ...accountLinks.map((link) => ({
          name: link.name,
          Component: () => (
            <DropdownMenuItem key={link.name} asChild>
              <Link href={link.href}>{link.name}</Link>
            </DropdownMenuItem>
          ),
        })),
        {
          name: 'logout',
          Component: () => (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                key="logout"
                className="text-destructive"
                onSelect={() => logout()}
              >
                {t('navbar.logout')}
              </DropdownMenuItem>
            </>
          ),
        },
      ],
    },
    language: {
      name: 'language',
      Component: () => null,
      Icon: LanguageIcon,
      children: availableLocales.map(({ label, value }) => ({
        name: label,
        Component: () => {
          const isSelected = value === currentLocale
          return (
            <button
              className="flex items-center p-4 justify-between w-full"
              onClick={() => setLocale(value)}
              key={value}
            >
              <span
                className={cn('font-semibold', {
                  'text-primary-alt': isSelected,
                })}
              >
                {label}
              </span>
              {isSelected && <Check />}
            </button>
          )
        },
      })),
    },
  }

  const depositItem = {
    name: 'portfolioDeposit',
    ...PORTFOLIO_LINKS.deposit,
  }
  const withdrawItem = {
    name: 'portfolioWithdraw',
    ...PORTFOLIO_LINKS.withdraw,
  }
  const portfolioItem = {
    name: 'portfolio',
    href: '/portfolio',
    Icon: Wallet,
  }

  const linkItems = {
    markets: {
      name: 'markets',
      href: '/',
      Icon: BarChart2,
    },
    trade: {
      name: 'trade',
      href: '/trade',
      Icon: ArrowDownUp,
    },
    accountNested: {
      name: 'account',
      href: '/account',
      Icon: User,
      children: [
        {
          name: 'accountProfile',
          ...ACCOUNT_LINKS.index,
        },
        {
          name: 'accountSecurity',
          ...ACCOUNT_LINKS.security,
        },
        {
          name: 'accountSettings',
          ...ACCOUNT_LINKS.settings,
        },
        {
          name: 'accountMore',
          ...ACCOUNT_LINKS.more,
        },
      ],
    },
    portfolio: portfolioItem,
    portfolioNested: {
      ...portfolioItem,
      children: [
        {
          name: 'portfolioOverview',
          ...PORTFOLIO_LINKS.index,
        },
        depositItem,
        withdrawItem,
        {
          name: 'portfolioTransactionHistory',
          ...PORTFOLIO_LINKS.history,
        },
      ],
    },
    deposit: depositItem,
    withdraw: withdrawItem,
  }

  return { linkItems, componentItems }
}

export const useDesktopNav = () => {
  const { linkItems: links, componentItems: components } = useNavItems()

  const leftColumnLinks = [links.markets, links.trade, links.portfolio]
  const rightColumnLinks = [
    components.accountDropdown,
    components.languageDropdown,
  ]

  const hotActions = {
    kyc: components.kyc,
    deposit: links.deposit,
  }

  return { leftColumnLinks, rightColumnLinks, hotActions }
}
export type UseDesktopNavReturnType = ReturnType<typeof useDesktopNav>

export const useMobileNav = () => {
  const { linkItems: links, componentItems: components } = useNavItems()
  const [nestedView, setNestedView] = useState<string | null>(null)

  const hotActions = {
    kyc: components.kyc,
    deposit: links.deposit,
    withdraw: links.withdraw,
  }

  const items: NavItem[] = [
    links.markets,
    links.trade,
    links.portfolioNested,
    links.accountNested,
    components.language,
  ]

  return {
    hotActions,
    items,
    nestedView,
    setNestedView,
  }
}

export type UseMobileNavReturnType = ReturnType<typeof useMobileNav>
export const useNavbar = () => {
  const t = useTranslations()
  return { t }
}
